import { api } from "@/lib/api";
import { NameSearchResult } from "@/types";
import { ActionIcon, Button, Combobox, InputBase, ScrollArea, Table, Tabs, Title, useCombobox } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconSearch, IconUserSearch } from "@tabler/icons-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { CountyMask } from "../CountySelect";


export interface GuardNameModuleProps {
    selectedCounty: CountyMask | null;
    onGuardName: (guardRecordSubscription: { county: string, name: string }) => void;
    guardedCountyNames?: { county: string, name: string }[];
}

const GuardNameModule: React.FC<GuardNameModuleProps> = ({ selectedCounty, onGuardName, guardedCountyNames }) => {
    const [guardedNames, setGuardedNames] = useState<{ county: string, name: string }[]>(guardedCountyNames ?? []);
    const [rawNameSearch, setRawNameSearch] = useState<string>('');
    const [nameSearch, setNameSearch] = useDebouncedState<string>('', 300);
    const [nameSearchResults, setNameSearchResults] = useState<NameSearchResult[]>([]);
    const onNameSearchChanges = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value.toLocaleUpperCase();
        setRawNameSearch(value);
        setNameSearch(value);
    }, [setNameSearch]);
    const nameSearchCombobox = useCombobox({
        onDropdownClose: () => {
            nameSearchCombobox.resetSelectedOption();
        }
    });
    const nameExists = (name: string): boolean => {
        return guardedNames?.some((guardNameSubscription) => guardNameSubscription.name === name.trim() && guardNameSubscription.county === selectedCounty?.name) ?? false;
    }
    useEffect(() => {
        if (selectedCounty) {
            setNameSearchResults([]);
            setRawNameSearch('');
            setGuardedNames(guardedCountyNames ?? []);
            nameSearchCombobox.resetSelectedOption();
        }
    }, [selectedCounty]);

    useEffect(() => {
        setGuardedNames(guardedCountyNames ?? []);
        setNameSearchResults([...nameSearchResults.map((item) => { item.loading = false; return item; })]);
    }, [guardedCountyNames])


    useEffect(() => {
        const searchNames = async () => {
            if (nameSearch.trim().length > 0) {
                try {
                    const searchResult: NameSearchResult[] = await api.get(`/search/name?SearchTerm=${nameSearch}&County=${selectedCounty?.name}`);
                    if (!searchResult.some((item => item.name.toLocaleUpperCase() === nameSearch.trim().toLocaleUpperCase()))) {
                        searchResult.unshift({ name: nameSearch.toLocaleUpperCase(), county: selectedCounty?.name ?? '', loading: false, custom: true });
                    }
                    setNameSearchResults(searchResult);
                    if (searchResult.length > 0) {
                    }
                } catch (error) {
                    console.error(error);
                    notifications.show({ title: 'Error', message: 'Failed to search Names', color: 'red', position: 'top-center',"aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
                }
            }
            else {
                setNameSearchResults([]);
            }
        }
        searchNames();
    }, [nameSearch]);
    return (
        <>
            <Title my={10} order={6}>Step 2: Enter a Name</Title>
            <Tabs defaultValue='nameSearch' variant="outline">
                <Tabs.List mb='md'>
                    <Tabs.Tab value="nameSearch" leftSection={<IconUserSearch size={20} />}>Name Search</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="nameSearch">
                    <Combobox
                        store={nameSearchCombobox}
                        withinPortal={false}
                        onOptionSubmit={(value) => {
                            setNameSearch(value);
                            setNameSearchResults(nameSearchResults.filter((item) => item.name === value));
                        }}
                    >
                        <Combobox.Target>
                            <InputBase
                                label="Name"
                                placeholder="Name"
                                description="Enter a personal, business, or entity name, then choose to guard the name you entered or select a match from the county records."
                                title="Name"
                                value={rawNameSearch}
                                type="text"
                                name="name"
                                data-1p-ignore
                                data-bwignore
                                data-lpignore="true"
                                data-form-type="other"
                                rightSection={<ActionIcon
                                    radius='xl'
                                    size='md'
                                    color="pq-red"
                                    aria-label="Search for name"
                                    variant="transparent">
                                    <IconSearch size={20} /></ActionIcon>
                                }
                                onChange={onNameSearchChanges}
                            />
                        </Combobox.Target>
                    </Combobox>
                    {nameSearchResults.length > 0 && <ScrollArea.Autosize mt={20}  mih={200} mah={250} type="auto">
                        <Table stickyHeader>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th></Table.Th>
                                    <Table.Th></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {nameSearchResults.map((result, i) => (
                                    <Fragment key={i}>
                                        {result.custom && 
                                            <Table.Tr h={90} style={{borderBottomColor: 'black', borderBottomWidth: '2px', borderBottomStyle: 'solid'}}>
                                                <Table.Td><strong>You Entered: </strong>{result.name}</Table.Td>
                                                <Table.Td>
                                                    <Button
                                                        variant='outline'
                                                        miw={120}
                                                        color={nameExists(result.name) ? 'green' : 'pq-red'}
                                                        loading={result.loading}
                                                        onClick={() => {
                                                            if (nameExists(result.name)) {
                                                                return;
                                                            }
                                                            nameSearchResults.forEach((item) => {
                                                                if (item.name == result.name && item.county == result.county) {
                                                                    item.loading = true
                                                                }
                                                            });
                                                            setNameSearchResults([...nameSearchResults])
                                                            onGuardName({ county: result.county, name: result.name });
                                                        }}
                                                    >{nameExists(result.name) ? 'Guarded' : 'Guard Name'}</Button>
                                                </Table.Td>
                                            </Table.Tr>}
                                        {!result.custom && 
                                            <Table.Tr>
                                                <Table.Td>{result.name}</Table.Td>
                                                <Table.Td>
                                                    <Button
                                                        variant='outline'
                                                        miw={120}
                                                        color={nameExists(result.name) ? 'green' : 'pq-red'}
                                                        loading={result.loading}
                                                        leftSection={nameExists(result.name) ? <IconCheck size={16} aria-hidden="true" /> : null}
                                                        onClick={() => {
                                                            if (nameExists(result.name)) {
                                                                return;
                                                            }
                                                            nameSearchResults.forEach((item) => {
                                                                if (item.name == result.name && item.county == result.county) {
                                                                    item.loading = true
                                                                }
                                                            });
                                                            setNameSearchResults([...nameSearchResults])
                                                            onGuardName({ county: result.county, name: result.name });
                                                        }}
                                                    >{nameExists(result.name) ? 'Guarded' : 'Guard Name'}</Button>
                                                </Table.Td>
                                            </Table.Tr>
                                        }
                                        {result.custom && <Table.Tr><Table.Td colSpan={2}><strong>Matching Names in the County Records:</strong></Table.Td><Table.Td></Table.Td></Table.Tr>}
                                    </Fragment>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea.Autosize>}
                </Tabs.Panel>
            </Tabs>
        </>
    )
}

export default GuardNameModule;